import { useState } from 'react';
import { useBilling } from '../../context/BillingContext';
import { PaymentMethodForm } from './PaymentMethodForm';
import type { SubscriptionPlan } from '../../types/billing';
import './SubscriptionPlans.css';

export function SubscriptionPlans() {
  const { plans, subscription, paymentMethods, subscribe, changePlan } = useBilling();
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const formatPrice = (price: number, currency: string) => {
    if (price === 0) return 'Free';
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: currency,
    }).format(price);
  };

  const handleSelectPlan = async (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setError(null);
    setSuccess(null);

    // If free plan, no payment needed
    if (plan.code === 'free') {
      return;
    }

    // If user already has a subscription, this is a plan change
    if (subscription) {
      setIsProcessing(true);
      try {
        await changePlan(plan.id);
        setSuccess(`Successfully changed to ${plan.name} plan!`);
        setSelectedPlan(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to change plan');
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    // If user has payment methods, use the default one
    const defaultPm = paymentMethods.find(pm => pm.isDefault);
    if (defaultPm) {
      setIsProcessing(true);
      try {
        await subscribe(plan.id, `pm_${defaultPm.id}`);
        setSuccess(`Successfully subscribed to ${plan.name}!`);
        setSelectedPlan(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to subscribe');
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    // Otherwise, show payment form
    setShowPaymentForm(true);
  };

  const handlePaymentSuccess = async (paymentMethodId: string) => {
    if (!selectedPlan) return;

    setIsProcessing(true);
    setShowPaymentForm(false);
    try {
      await subscribe(selectedPlan.id, paymentMethodId);
      setSuccess(`Successfully subscribed to ${selectedPlan.name}!`);
      setSelectedPlan(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to subscribe');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="subscription-plans">
      <h2>Choose Your Plan</h2>
      <p className="plans-description">
        Select the plan that best fits your gaming style. All plans can be cancelled anytime.
      </p>

      {error && <div className="plans-error">{error}</div>}
      {success && <div className="plans-success">{success}</div>}

      <div className="plans-grid">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`plan-card ${plan.isCurrentPlan ? 'current' : ''} ${
              plan.isRecommended ? 'recommended' : ''
            } ${selectedPlan?.id === plan.id ? 'selected' : ''}`}
          >
            {plan.isRecommended && <div className="plan-badge">Popular</div>}
            {plan.isCurrentPlan && <div className="plan-badge current">Current Plan</div>}

            <h3 className="plan-name">{plan.name}</h3>
            <div className="plan-price">
              <span className="price-amount">{formatPrice(plan.priceMonthly, plan.currency)}</span>
              {plan.priceMonthly > 0 && <span className="price-period">/month</span>}
            </div>

            {plan.description && <p className="plan-description">{plan.description}</p>}

            <ul className="plan-features">
              {plan.features.map((feature, idx) => (
                <li key={idx}>
                  <span className="feature-check">&#10003;</span>
                  {feature}
                </li>
              ))}
            </ul>

            <button
              className={`plan-button ${plan.isCurrentPlan ? 'current' : ''}`}
              onClick={() => handleSelectPlan(plan)}
              disabled={plan.isCurrentPlan || isProcessing}
            >
              {isProcessing && selectedPlan?.id === plan.id
                ? 'Processing...'
                : plan.isCurrentPlan
                ? 'Current Plan'
                : subscription
                ? 'Switch to This Plan'
                : plan.code === 'free'
                ? 'Get Started'
                : 'Subscribe'}
            </button>
          </div>
        ))}
      </div>

      {showPaymentForm && selectedPlan && (
        <div className="payment-modal-overlay">
          <div className="payment-modal">
            <h3>Add Payment Method</h3>
            <p>Subscribe to {selectedPlan.name} for {formatPrice(selectedPlan.priceMonthly, selectedPlan.currency)}/month</p>
            <PaymentMethodForm
              onSuccess={handlePaymentSuccess}
              onCancel={() => {
                setShowPaymentForm(false);
                setSelectedPlan(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
